/**
 * Work in this page that a reload would lose or cut short. The app reloads itself onto a newer
 * Paige between screens (apps/web/src/version, docs/engineering/web.md, "When Paige is updated"),
 * and asks `pageHold()` first: while anything here holds the page it only says Paige was updated
 * and leaves the reload to the person.
 *
 * - `upload`: files on their way into the job's workspace (upload.ts);
 * - `run`: a reading being followed (process-runs.ts); the run itself goes on on the host and a
 *   reload re-attaches to it, but the screen that watches it would start again, so it holds too;
 * - `pages-waiting`: pages added but not read yet, which live only in this page (browser-captures.ts);
 * - `setup-edits`: a job's setup changed and not saved (renderer/App.tsx, `setupHasChanges`);
 * - `review-edits`: field drafts or a save under way in review, what CapturePoint's close
 *   permission vetoed a window close for (renderer/close-permission.ts).
 */
export type PageHoldReason = 'upload' | 'run' | 'pages-waiting' | 'setup-edits' | 'review-edits';

interface Hold {
  reason: PageHoldReason;
  /** Asked each time; a hold with no `read` holds until it is released. */
  read?: () => boolean;
}

const holds = new Set<Hold>();

/** Holds the page until the returned release is called (calling it again does nothing). */
export function holdPage(reason: PageHoldReason): () => void {
  return addHold({ reason });
}

/** Holds the page whenever `read` says so, until the returned release is called. */
export function registerPageHold(reason: PageHoldReason, read: () => boolean): () => void {
  return addHold({ reason, read });
}

/** `work` with the page held for as long as it runs, however it ends. */
export async function whileHeld<T>(reason: PageHoldReason, work: () => Promise<T>): Promise<T> {
  const release = holdPage(reason);
  try {
    return await work();
  } finally {
    release();
  }
}

/** The first thing holding the page, or null when a reload would lose nothing here. */
export function pageHold(): PageHoldReason | null {
  for (const hold of holds) {
    if (!hold.read || readSafely(hold.read)) return hold.reason;
  }
  return null;
}

function addHold(hold: Hold): () => void {
  holds.add(hold);
  return () => {
    holds.delete(hold);
  };
}

/** A read that throws cannot say the page is free, so it holds. */
function readSafely(read: () => boolean): boolean {
  try {
    return read();
  } catch {
    return true;
  }
}
